import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { AccessRole, AccessService, AccessUser } from '../../_services/access.service';

// add: new user; edit: change role(s); approve: give an access request its role(s).
export type UserFormMode = 'add' | 'edit' | 'approve';

export interface UserFormDialogData {
  readonly mode: UserFormMode;
  readonly user: AccessUser | null;
  readonly roles: AccessRole[];
  readonly multipleRoles: boolean;
}

// Saves through AccessService itself so backend validation errors show in the dialog;
// closes with the saved user.
@Component({
  selector: 'app-user-form-dialog',
  imports: [
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatCheckboxModule,
  ],
  templateUrl: './user-form-dialog.html',
  styleUrl: './user-form-dialog.scss',
})
export class UserFormDialog {
  protected readonly data = inject<UserFormDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<UserFormDialog, AccessUser>);
  private readonly accessService = inject(AccessService);

  protected email = this.data.user?.email ?? '';
  protected name = this.data.user?.displayName ?? '';
  protected readonly selectedRoles = signal<string[]>([...(this.data.user?.roles ?? [])]);
  protected readonly error = signal('');
  protected readonly saving = signal(false);

  protected readonly titleKey = {
    add: 'access.userForm.addTitle',
    edit: 'access.userForm.editTitle',
    approve: 'access.userForm.approveTitle',
  }[this.data.mode];

  protected selectRole(code: string): void {
    this.selectedRoles.set([code]);
  }

  protected toggleRole(code: string, checked: boolean): void {
    this.selectedRoles.update((roles) => (checked ? [...roles, code] : roles.filter((r) => r !== code)));
  }

  protected save(): void {
    if (this.selectedRoles().length === 0) {
      this.error.set(this.data.multipleRoles ? 'access.userForm.chooseRoles' : 'access.userForm.chooseRole');
      return;
    }

    this.saving.set(true);
    this.error.set('');
    const save$ =
      this.data.mode === 'add'
        ? this.accessService.createUser(this.email.trim(), this.name.trim(), this.selectedRoles())
        : this.accessService.updateUser(this.data.user!.id, this.name.trim(), this.selectedRoles());

    save$.subscribe({
      next: (saved) => this.dialogRef.close(saved),
      error: (error) => {
        this.error.set(AccessService.errorMessage(error));
        this.saving.set(false);
      },
    });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
